import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Form, Input, Level, Toasts, Notification } from '@deseretbook/react-ui'
import { DeseretBook } from '../../../dbcom/api'
import { withCheckout } from '../CheckoutContext'
import { Logger, formatErrorMessage } from '../../../dbcom'

const logger = new Logger({ context: 'PlatinumPointsPaymentMethod' })

/**
 * Formats Platinum Points to the proper format to display to users.
 *
 * TODO This should be moved to a generic utils location for proper reuse.
 *
 * @param {string|number} [amount = 0] - number of points
 * @returns {string} Formatted Platinum Points
 */
const displayPlatinumPoints = (amount = 0) => `$${(Number(amount) / 100).toFixed(2)}`

/**
 * Platinum Points payment method to be used as part of the Payment Step in
 * checkout.
 */
class PlatinumPointsPaymentMethod extends Component {
  /**
   * Component constructor
   */
  constructor() {
    super()

    this.state = {
      /** If the payment method is currently being applied. */
      isSubmitting: false,
      /** Current form values. */
      form: {
        points: '',
      },
    }

    this.onFormChange = this.onFormChange.bind(this)
    this.resetForm = this.resetForm.bind(this)
    this.applyPoints = this.applyPoints.bind(this)
  }

  /**
   * onChange handler for the payment method form. Parses inputed value to
   * ensure users can only enter numbers.
   */
  onFormChange({ target }) {
    const { user } = this.props
    const { name, value } = target

    let parsedValue = value

    if (name === 'points') {
      parsedValue = value
        .replace(/\D/g, '') // Remove all non-digit characters
        .replace(/^[0]+/g, '') // Remove all leading 0's

      // Max out the inputed value to the total number of points the user has.
      parsedValue = String(Math.max(Number(parsedValue, user.points)))
    }

    this.setState(({ form }) => ({
      form: {
        ...form,
        [name]: parsedValue,
      },
    }))
  }

  /**
   * Resets the payment method form.
   */
  resetForm() {
    this.setState({
      isSubmitting: false,
      form: {
        points: '',
      },
    })
  }

  /**
   * Applies specified Platinum Points to the current order.
   */
  applyPoints() {
    const {
      order,
      updateOrder,
      startLoading,
      stopLoading,
    } = this.props

    if (!this.state.form.points.length) {
      Toasts.warn('Missing Points', 'You must specify how many points you want to use first.')

      return
    }

    startLoading(() => {
      this.setState({ isSubmitting: true }, async () => {
        const { form } = this.state

        try {
          const updatedOrder = await DeseretBook
            .order(order)
            .applyPlatinumPoints(form.points)

          updateOrder(updatedOrder) // TODO should this try to advance?
          this.resetForm()
          stopLoading()
          Toasts.success(`${displayPlatinumPoints(form.points)} Platinum Points applied`)
        } catch ({ message, errors }) {
          this.setState({ isSubmitting: false })
          stopLoading()
          Toasts.danger('Unable to apply Platinum Points', formatErrorMessage(message, errors))
          logger.error(`Unable to apply Platinum Points: "${formatErrorMessage(message, errors)}"`, { errors, form, order })
        }
      })
    })
  }

  /**
   * Determines how the component should be rendered.
   * @returns {function} - PlatinumPointsPaymentMethod component.
   */
  render() {
    const { user, status } = this.props
    const { isSubmitting, form } = this.state

    const platinumPointsAreProcessable = status && status.services && status.services.relate

    return (
      <Form isSubmitting={isSubmitting} onSubmit={this.applyPoints}>
        {
          !platinumPointsAreProcessable
          ? (
            <Notification
              type="warning"
              titleContent="Unable to Process Platinum Points"
            >
              Sorry! It looks like we&#39;re having troubles processing Platinum Points
              right now. Please refresh the page or try again in a little bit.
            </Notification>
          )
          : null
        }
        <Input
          name="points"
          type="tel"
          label="Platinum Points"
          value={
            form.points
              ? displayPlatinumPoints(form.points)
              : ''
          }
          onChange={this.onFormChange}
          disabled={!platinumPointsAreProcessable}
          hint={`You have ${displayPlatinumPoints(user.points)} available`}
        />
        <Level>
          <Level.Right>
            <Form.SubmitButton
              color="primary"
              disabled={!platinumPointsAreProcessable || !form.points.length}
            >
              Apply
            </Form.SubmitButton>
          </Level.Right>
        </Level>
      </Form>
    )
  }
}

PlatinumPointsPaymentMethod.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
  order: PropTypes.shape({
    number: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
  status: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.objectOf(PropTypes.bool),
  ])),
  updateOrder: PropTypes.func,
  startLoading: PropTypes.func,
  stopLoading: PropTypes.func,
}

PlatinumPointsPaymentMethod.defaultProps = {
  user: {},
  order: {},
  status: {},
  updateOrder: () => {},
  startLoading: () => {},
  stopLoading: () => {},
}


export default withCheckout(PlatinumPointsPaymentMethod)
